/**
 * AttachmentsPanel — files on a project/opportunity (shared: Projects tab and
 * Opportunities tab are the same rows keyed by id). Self-contained: own state,
 * lists on mount, uploads multipart then refetches.
 *
 * Every upload is LABELED (Invoice / PO / Quote + its number, or General) so
 * the Summary tab can deep-link numbers straight to their file.
 *
 * API: GET/POST /api/projects/:projectId/attachments (fields "file",
 *      "doc_type", "doc_ref"),
 *      GET       /api/attachments/file/:attId  (?inline=1 to preview),
 *      DELETE    /api/attachments/:attId       (open to every role that can
 *                upload; `canDelete` just hides the button)
 *
 * Auth is automatic — window.fetch is patched (src/index.js) to attach the
 * Entra bearer token. For upload we do NOT set Content-Type: the browser must
 * set the multipart boundary itself.
 *
 * The uploads, labels/links, the uploader box and the file list live in
 * ./attachmentsPanel/ (moved out verbatim); this file keeps the state, the
 * folder grid and the layout.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { pickDocFor, findLinkedRfq } from '../../utils/attachmentPreview';
import DocFolderGrid from './DocFolderGrid';
import { folderKeyOf, LINK_FOLDERS } from '../../data/docFolders';
import { useApiBase } from '../../context/DataScope';
import { useLiveRefresh, matchRecord, matchKind } from '../../live/useLive';
import { GREEN, DropNotice } from './attachmentsPanel/panelParts';
import { useAttachmentUploads } from './attachmentsPanel/useAttachmentUploads';
import { useAttachmentLabels } from './attachmentsPanel/useAttachmentLabels';
import { renderUploader } from './attachmentsPanel/Uploader';
import { renderFileList } from './attachmentsPanel/FileList';
export { DocTag } from './attachmentsPanel/panelParts';

/**
 * When `projectId` is empty the record does not exist yet (a new opportunity
 * being typed). Files are then HELD in the browser and handed to `onStage`
 * instead of uploaded; the modal pushes them up once the save returns an id.
 * Auto-saving a half-typed opportunity just to have somewhere to put a file
 * would leave abandoned rows in the bid pipeline, which the year totals count.
 */
export default function AttachmentsPanel({ projectId, canDelete = false, onChanged, suggest, staged, onStage }) {
  const pending = !projectId;
  const [files, setFiles] = useState([]);
  const [subFolders, setSubFolders] = useState([]);
  // null = the folder grid; { ...folder, subId } = inside one.
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);
  const [docType, setDocType] = useState('');
  const [docRef, setDocRef] = useState('');
  const [suggested, setSuggested] = useState(null); // {type, ref, why} shown as a hint
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [moving, setMoving] = useState(null); // { id, folder, subfolder_id }
  const inputRef = useRef(null);
  const base = useApiBase(); // '/api', or the KSA tab's own Doc Hub

  const load = useCallback(async () => {
    // a new record has nothing to fetch yet - without this the folder list
    // said "Loading..." forever and hid the files staged for upload
    if (!projectId) {
      setLoading(false);
      return;
    }
    const pid = encodeURIComponent(projectId);
    try {
      const [fr, dr] = await Promise.all([fetch(`${base}/projects/${pid}/attachments`), fetch(`${base}/projects/${pid}/folders`)]);
      if (fr.ok) setFiles(await fr.json());
      if (dr.ok) setSubFolders(await dr.json());
    } catch {
      /* offline/dev */
    } finally {
      setLoading(false);
    }
  }, [projectId, base]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);
  // Someone else's upload, relabel, move or new folder on this record shows
  // up without a refresh (src/live). A file changed by its own id does not
  // say which record it is on, so those re-read too.
  const recordFiles = matchRecord(base, projectId, ['attachments', 'folders']);
  const anyFile = matchKind(base, 'attachments');
  useLiveRefresh(null, load, { match: (e) => recordFiles(e) || anyFile(e) });

  const stagedRows = (staged || []).map((sf, i) => ({
    id: `staged-${i}`,
    filename: sf.file.name,
    size: sf.file.size,
    mime: sf.file.type,
    uploaded_by: 'not uploaded yet',
    created_at: null,
    doc_type: sf.doc_type,
    doc_ref: sf.doc_ref,
    folder: sf.folder,
    subfolder_id: null,
    _staged: true,
    _index: i,
  }));
  const all = pending ? stagedRows : files;

  /** How many files sit in a folder (subId null) or one of its sub-folders. */
  const countFor = useCallback(
    (folderKey, subId) =>
      (pending ? stagedRows : files).filter((f) => folderKeyOf(f) === folderKey && (subId == null || f.subfolder_id === subId)).length,
    [pending, stagedRows, files]
  );

  const visible = view ? all.filter((f) => folderKeyOf(f) === view.key && (view.subId == null || f.subfolder_id === view.subId)) : [];

  const addSub = useCallback(
    async (folderKey, name) => {
      try {
        const r = await fetch(`${base}/projects/${encodeURIComponent(projectId)}/folders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folder_key: folderKey, name }),
        });
        if (!r.ok) return (await r.json().catch(() => null))?.error || 'Could not add that folder.';
        await load();
        return '';
      } catch {
        return 'Could not add that folder — check your connection.';
      }
    },
    [projectId, load, base]
  );

  const deleteSub = useCallback(
    async (sub) => {
      const n = countFor(sub.folder_key, sub.id);
      const warn = n
        ? `Delete "${sub.name}"?\n\nIts ${n} file${n === 1 ? '' : 's'} move back to the main folder — nothing is deleted.`
        : `Delete "${sub.name}"?`;
      if (!window.confirm(warn)) return;
      try {
        const r = await fetch(`${base}/attachments/folders/${encodeURIComponent(sub.id)}`, { method: 'DELETE' });
        if (r.ok) {
          setView((v) => (v && v.subId === sub.id ? { ...v, subId: null } : v));
          await load();
        }
      } catch {
        /* noop */
      }
    },
    [countFor, load, base]
  );

  /** Refile one card into another folder / sub-folder. */
  const saveMove = useCallback(async () => {
    if (!moving) return;
    try {
      const r = await fetch(`${base}/attachments/${encodeURIComponent(moving.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(moving.subfolder_id ? { subfolder_id: moving.subfolder_id } : { folder: moving.folder || null }),
      });
      if (r.ok) {
        setMoving(null);
        await load();
        if (onChanged) onChanged();
      }
    } catch {
      /* noop */
    }
  }, [moving, load, onChanged, base]);

  const clearPicker = useCallback(() => {
    setPicked(null);
    setDocType('');
    setDocRef('');
    setError('');
    setSuggested(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const { rootRef, bulk, notice, setNotice, handleDrop, setDropTarget, dropTarget, pickFile, upload, ready, remove } = useAttachmentUploads(
    {
      setPicked,
      setError,
      suggest,
      setSuggested,
      setDocType,
      setDocRef,
      inputRef,
      docType,
      picked,
      docRef,
      pending,
      onStage,
      staged,
      base,
      projectId,
      load,
      onChanged,
      view,
      uploading,
      clearPicker,
      setUploading,
    }
  );
  const { editing, setEditing, saveLabel, linking, setLink } = useAttachmentLabels({ base, load, onChanged });
  // In Quotes / Purchase Orders / RFQ emails: which file this record's number
  // opens, so every row can say whether it is the one (and let you change it).
  const linkType = view ? LINK_FOLDERS[view.key] : null;
  const linkNumber = linkType === 'po' ? String(suggest?.po || '').trim() : String(suggest?.quote || '').trim();
  const linkName = linkType === 'po' ? `PO ${linkNumber}` : `Quote ${linkNumber}`;
  const opens = linkType && linkType !== 'rfq' && linkNumber ? pickDocFor(all, linkType, linkNumber) : null;
  const linkedRfq = linkType === 'rfq' ? findLinkedRfq(all) : null;
  const canLink = !!linkType && !pending;

  return (
    <div ref={rootRef} style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: GREEN, letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 10 }}>
        Documents
      </div>

      {/* A drop onto a folder card uploads from the grid: say how it went here,
          the in-folder progress line is not on screen. */}
      {!view && bulk && (
        <div style={{ fontSize: 10.5, fontWeight: 700, color: GREEN, marginBottom: 8 }}>
          {bulk.note || `Uploading ${bulk.done} of ${bulk.total}…`}
        </div>
      )}
      <DropNotice notice={notice} onClose={() => setNotice(null)} />

      <DocFolderGrid
        view={view}
        subFolders={subFolders}
        countFor={countFor}
        canManage={!!projectId}
        onOpen={(folder, subId) => setView(folder ? { ...folder, subId: subId ?? null } : null)}
        onAddSub={addSub}
        onDeleteSub={deleteSub}
        onDropFiles={(e, folderKey) => handleDrop(e, folderKey, null)}
      />

      {/* Uploading and the file list only exist INSIDE a folder — that is what
          stops everything landing in one undifferentiated pile again. */}
      {!view ? null : (
        <>
          {renderUploader({
            setDropTarget,
            view,
            handleDrop,
            dropTarget,
            bulk,
            inputRef,
            pickFile,
            picked,
            suggested,
            docType,
            setDocType,
            setError,
            docRef,
            setDocRef,
            error,
            clearPicker,
            upload,
            ready,
            uploading,
          })}

          {canLink && !loading && visible.length > 0 && (
            <div
              style={{
                fontSize: 10.5,
                color: '#5A6557',
                background: '#F6F9F3',
                border: '1px solid #E1E9DA',
                borderRadius: 6,
                padding: '7px 10px',
                marginBottom: 8,
                lineHeight: 1.45,
              }}
            >
              {linkType === 'rfq' ? (
                linkNumber ? (
                  <>
                    Mark which of these is the RFQ for <b>Quote {linkNumber}</b> with <b>Link</b>. It shows on the project summary.
                  </>
                ) : (
                  <>
                    Mark which of these is this record&apos;s RFQ with <b>Link</b>.
                  </>
                )
              ) : linkNumber ? (
                <>
                  The <b>{linkName}</b> on the Projects and Opportunities tables opens the file marked below. Several files for one number
                  (a PDF, the .xlsm, a Rev 1)? Pick the right one with <b>Link</b>.
                </>
              ) : (
                <>This record has no {linkType === 'po' ? 'PO' : 'quote'} number yet, so there is nothing to link a file to.</>
              )}
            </div>
          )}

          {renderFileList({
            loading,
            visible,
            view,
            canLink,
            opens,
            linkName,
            linkedRfq,
            linkNumber,
            linkType,
            editing,
            setEditing,
            saveLabel,
            moving,
            setMoving,
            subFolders,
            saveMove,
            base,
            linking,
            setLink,
            canDelete,
            remove,
          })}
        </>
      )}
    </div>
  );
}
